// 蓝宝 AI：可拖动的全站悬浮入口；点击或键盘激活打开聊天。
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Fab, Avatar } from '@mui/material';
import LanBaoDrawer from '@/components/buyer/LanBaoDrawer';
import lanBaoLogo from '@/assets/logo.jpg';
import { usePrefs } from '@/store/BuyerPrefsContext';

import { tr } from '@/i18n';
const POSITION_KEY = 'buyer-lanbao-position';
const SIZE = 56;
const EDGE = 8;
type Position = { x: number; y: number };
type Gesture = { id: number; startX: number; startY: number; origin: Position };

function constrain(position: Position): Position {
  const viewport = window.visualViewport;
  const left = (viewport?.offsetLeft ?? 0) + EDGE;
  const top = (viewport?.offsetTop ?? 0) + EDGE;
  const right = left + (viewport?.width ?? window.innerWidth) - SIZE - EDGE * 2;
  const bottom = top + (viewport?.height ?? window.innerHeight) - SIZE - EDGE * 2;
  const tabTop = document.querySelector('.buyer-bottom-tabs')?.getBoundingClientRect().top;
  return {
    x: Math.max(left, Math.min(position.x, right)),
    y: Math.max(top, Math.min(position.y, tabTop == null ? bottom : Math.min(bottom, tabTop - SIZE - EDGE))),
  };
}

export default function LanBaoFab() {
  const { t, lang } = usePrefs();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [dragging, setDragging] = useState(false);
  const gesture = useRef<Gesture | null>(null);
  const suppressClick = useRef(false);
  const button = useRef<HTMLButtonElement>(null);

  const persist = useCallback((point: Position) => {
    try { sessionStorage.setItem(POSITION_KEY, JSON.stringify(point)); } catch { /* Storage can be disabled. */ }
  }, []);

  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(POSITION_KEY) || 'null');
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) setPosition(constrain(saved));
    } catch { /* Keep the default position if stored data is invalid. */ }
    const onResize = () => setPosition((point) => point ? constrain(point) : null);
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('scroll', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('scroll', onResize);
    };
  }, []);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener('open-lanbao', openHandler);
    return () => window.removeEventListener('open-lanbao', openHandler);
  }, []);

  const startDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!event.isPrimary || event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    gesture.current = { id: event.pointerId, startX: event.clientX, startY: event.clientY, origin: { x: rect.left, y: rect.top } };
    suppressClick.current = false;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const active = gesture.current;
    if (!active || active.id !== event.pointerId) return;
    const dx = event.clientX - active.startX;
    const dy = event.clientY - active.startY;
    if (!suppressClick.current && Math.hypot(dx, dy) < 6) return;
    suppressClick.current = true;
    setDragging(true);
    setPosition(constrain({ x: active.origin.x + dx, y: active.origin.y + dy }));
  };

  const finishDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const active = gesture.current;
    if (!active || active.id !== event.pointerId) return;
    if (suppressClick.current) {
      const rect = event.currentTarget.getBoundingClientRect();
      const next = constrain({ x: rect.left, y: rect.top });
      setPosition(next);
      persist(next);
    }
    gesture.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return <>
    <Fab
      ref={button}
      aria-label={t('comp.lanbaoFabLabel')}
      title={tr('comp.clickToChatDragTo')}
      onPointerDown={startDrag}
      onPointerMove={moveDrag}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onLostPointerCapture={() => { gesture.current = null; setDragging(false); }}
      onClick={(event) => {
        if (suppressClick.current && event.detail !== 0) {
          event.preventDefault();
          suppressClick.current = false;
          return;
        }
        setOpen(true);
      }}
      sx={{
        position: 'fixed',
        left: position ? position.x : 'auto',
        top: position ? position.y : 'auto',
        bottom: position ? 'auto' : { xs: 'calc(82px + env(safe-area-inset-bottom, 0px))', md: 28 },
        right: position ? 'auto' : { xs: 16, md: 28 },
        touchAction: 'none', userSelect: 'none', cursor: dragging ? 'grabbing' : 'grab',
        zIndex: 20, width: SIZE, height: SIZE, bgcolor: '#FFFFFF',
        boxShadow: dragging ? '0 8px 24px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.15)',
        overflow: 'hidden', '&:hover': { bgcolor: '#F5F5F5' },
      }}
    >
      <Avatar src={lanBaoLogo} alt="" sx={{ width: '100%', height: '100%', bgcolor: 'transparent', pointerEvents: 'none' }} />
    </Fab>
    <LanBaoDrawer open={open} onClose={() => setOpen(false)} />
  </>;
}
